import type {
  AgentCloudTestConfigChange,
  AgentCloudTestConfigFields,
  AgentExcludedTimeRange,
  AgentExcludedWeeklyTimeRange,
  AgentTestOperation,
  Period,
} from '../../../../types';
import {
  getUnmappedConfigFieldEntries,
  humanizeConfigFieldKey,
  isAgentCloudTestCreateChange,
} from '../../../../types';

const formatPeriod = (p: Period | null | undefined): string =>
  p && typeof p.count === 'number' && typeof p.period === 'string'
    ? `${p.count} ${p.period}`
    : 'none';

const formatPercent = (value: number | null | undefined): string =>
  typeof value === 'number' ? `${value}%` : 'none';

const formatWeeklyTimeRange = (range: AgentExcludedWeeklyTimeRange): string => {
  if (range.start && range.end) {
    const startDay = range.start.day_of_week;
    const startTime = range.start.time_of_day;
    const endDay = range.end.day_of_week;
    const endTime = range.end.time_of_day;
    if (startDay && startTime && endDay && endTime) {
      const timezone = range.timezone ? ` (${range.timezone})` : '';
      return `${startDay} ${startTime} → ${endDay} ${endTime}${timezone}`;
    }
  }
  if (range.startDay && range.startTime && range.endDay && range.endTime) {
    return `${range.startDay} ${range.startTime} → ${range.endDay} ${range.endTime}`;
  }
  try {
    return JSON.stringify(range);
  } catch {
    return 'range';
  }
};

const formatAbsoluteTimeRange = (range: AgentExcludedTimeRange): string => {
  if (range.start_time && range.end_time) {
    return `${range.start_time} → ${range.end_time}`;
  }
  try {
    return JSON.stringify(range);
  } catch {
    return 'range';
  }
};

const formatTimeRanges = (
  ranges:
    | AgentExcludedWeeklyTimeRange[]
    | AgentExcludedTimeRange[]
    | null
    | undefined,
  kind: 'weekly' | 'absolute',
): string => {
  if (!ranges || ranges.length === 0) return 'none';
  if (kind === 'weekly') {
    return (ranges as AgentExcludedWeeklyTimeRange[])
      .map(formatWeeklyTimeRange)
      .join('; ');
  }
  return (ranges as AgentExcludedTimeRange[])
    .map(formatAbsoluteTimeRange)
    .join('; ');
};

const formatUnknownValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'none';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'none';
    return value.map(formatUnknownValue).join('; ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export const formatCloudTestConfigFieldsSummary = (
  fields: AgentCloudTestConfigFields,
): string => {
  const parts: string[] = [];
  const handledKeys = new Set<string>();
  const pushHandled = (key: keyof AgentCloudTestConfigFields, part: string) => {
    handledKeys.add(key);
    parts.push(part);
  };

  if (fields.sensitivity)
    pushHandled(
      'sensitivity',
      `sensitivity: ${fields.sensitivity.current ?? 'none'} \u2192 ${
        fields.sensitivity.new
      }`,
    );
  if (fields.threshold)
    pushHandled(
      'threshold',
      `threshold: ${formatPeriod(
        fields.threshold.current,
      )} \u2192 ${formatPeriod(fields.threshold.new)}`,
    );
  if (fields.anomalyDirection)
    pushHandled(
      'anomalyDirection',
      `direction: ${fields.anomalyDirection.current ?? 'none'} \u2192 ${
        fields.anomalyDirection.new
      }`,
    );
  if (fields.detectionPeriod)
    pushHandled(
      'detectionPeriod',
      `detection: ${formatPeriod(
        fields.detectionPeriod.current,
      )} \u2192 ${formatPeriod(fields.detectionPeriod.new)}`,
    );
  if (fields.spikeThreshold)
    pushHandled(
      'spikeThreshold',
      `spike: ${formatPercent(
        fields.spikeThreshold.current,
      )} \u2192 ${formatPercent(fields.spikeThreshold.new)}`,
    );
  if (fields.dropThreshold)
    pushHandled(
      'dropThreshold',
      `drop: ${formatPercent(
        fields.dropThreshold.current,
      )} \u2192 ${formatPercent(fields.dropThreshold.new)}`,
    );
  if (fields.trainingPeriod)
    pushHandled(
      'trainingPeriod',
      `training: ${formatPeriod(
        fields.trainingPeriod.current,
      )} \u2192 ${formatPeriod(fields.trainingPeriod.new)}`,
    );
  if (fields.failureThresholdGrace)
    pushHandled(
      'failureThresholdGrace',
      `failure grace: ${formatPeriod(
        fields.failureThresholdGrace.current,
      )} \u2192 ${formatPeriod(fields.failureThresholdGrace.new)}`,
    );
  if (fields.fixedThreshold)
    pushHandled(
      'fixedThreshold',
      `fixed threshold: ${formatPeriod(
        fields.fixedThreshold.current,
      )} \u2192 ${formatPeriod(fields.fixedThreshold.new)}`,
    );
  if (fields.excludedWeeklyTimeRanges)
    pushHandled(
      'excludedWeeklyTimeRanges',
      `excluded weekly: ${formatTimeRanges(
        fields.excludedWeeklyTimeRanges.current,
        'weekly',
      )} \u2192 ${formatTimeRanges(
        fields.excludedWeeklyTimeRanges.new,
        'weekly',
      )}`,
    );
  if (fields.excludedTimeRanges)
    pushHandled(
      'excludedTimeRanges',
      `excluded times: ${formatTimeRanges(
        fields.excludedTimeRanges.current,
        'absolute',
      )} \u2192 ${formatTimeRanges(fields.excludedTimeRanges.new, 'absolute')}`,
    );
  if (fields.severity)
    pushHandled(
      'severity',
      `severity: ${fields.severity.current ?? 'none'} \u2192 ${
        fields.severity.new
      }`,
    );
  if (fields.owners)
    pushHandled(
      'owners',
      `owners: ${fields.owners.current?.length ?? 0} \u2192 ${
        fields.owners.new?.length ?? 0
      }`,
    );
  if (fields.tags)
    pushHandled(
      'tags',
      `tags: ${fields.tags.current?.length ?? 0} \u2192 ${
        fields.tags.new?.length ?? 0
      }`,
    );
  if (fields.enabled)
    pushHandled('enabled', fields.enabled.new ? 'enable' : 'disable');
  if (fields.muted) pushHandled('muted', fields.muted.new ? 'mute' : 'unmute');
  if (fields.baselineMode)
    pushHandled(
      'baselineMode',
      `baseline: ${fields.baselineMode.current ?? 'none'} \u2192 ${
        fields.baselineMode.new
      }`,
    );
  if (fields.enforcementLevel)
    pushHandled(
      'enforcementLevel',
      `enforcement: ${fields.enforcementLevel.current ?? 'none'} \u2192 ${
        fields.enforcementLevel.new
      }`,
    );
  if (fields.baseline)
    pushHandled(
      'baseline',
      `baseline columns: ${fields.baseline.current?.length ?? 0} \u2192 ${
        fields.baseline.new?.length ?? 0
      }`,
    );

  for (const [key, change] of getUnmappedConfigFieldEntries(
    fields,
    handledKeys,
  )) {
    parts.push(
      `${humanizeConfigFieldKey(key)}: ${formatUnknownValue(
        change.current,
      )} \u2192 ${formatUnknownValue(change.new)}`,
    );
  }

  return parts.join(', ') || 'no changes';
};

export const formatCloudTestChangeSummary = (
  change: AgentCloudTestConfigChange,
  operation: AgentTestOperation,
): string => {
  if (operation === 'delete') {
    return 'delete test';
  }
  if (isAgentCloudTestCreateChange(change)) {
    const configSummary = formatCloudTestConfigFieldsSummary(change);
    return `Create ${change.testType} test — ${configSummary}`;
  }
  return formatCloudTestConfigFieldsSummary(change);
};

export const formatCloudTestChangeLabel = (
  change: AgentCloudTestConfigChange,
): string => {
  if (isAgentCloudTestCreateChange(change)) {
    return change.assetId;
  }
  return change.testId;
};
